import { apiFetch, authHeaders } from '../client';

/**
 * Client for the backend Nueva Cotización transaction endpoint.
 *
 * The backend validates + runs the multi-list transaction; the client
 * just shapes the payload from the page state. Kept separate from the
 * `SharePointListClient` helpers because this endpoint isn't a single
 * SharePoint list.
 */

export type VentaLinePayload = {
  articuloId: string;
  articulo: string;
  monto: number;
  cantidad: number;
  /** IVA rate as a percentage. AR: 21 / 10.5; MX: 16 / 8. Lines with
   *  Exento IVA upcast to the country default before sending — see
   *  `NuevaCotizacionPage` payload mapping. */
  iva: number;
  descuento: number;
  esRegalo: boolean;
  /** "Lleva garantía": los meses los pone el server desde el ABM de Artículos. */
  llevaGarantia?: boolean;
  /** yyyy-mm-dd. Sólo si el producto no tiene duración: vencimiento manual. */
  garantiaVence?: string | null;
  subtotal: number;
  subtotalDescontado: number;
  total: number;
  needsAuthorization: boolean;
  tipoDescuentoLabel: string;
  motivoAutorizacion?: string;
  /** Active campaign pct for this article, or null. Server skips writing a
   *  Descuentos row when descuento matches the campaign pct (mirrors
   *  PowerApps, which only writes a 15.Descuentos row when the discount
   *  differs from the campaign). */
  campaignPct?: number | null;
};

export type VentaHeaderPayload = {
  clienteId: string;
  cliente: string;
  correo?: string | null;
  referido?: string | null;
  moneda: 'ARS' | 'MXN' | 'USD';
  subtotal: number;
  /** AR backwards-compat: IVA totals split by rate. MX rows leave
   *  these at 0 — server reads `ivaByRate` if present, else falls
   *  back to these. */
  iva21: number;
  iva105: number;
  total: number;
};

export type CreateVentaPayload = {
  header: VentaHeaderPayload;
  lines: VentaLinePayload[];
};

export type CreateVentaResponse = {
  id: string;
  nrCotizacion: string;
  status: string;
  detalleIds: string[];
};

export async function createVenta(payload: CreateVentaPayload): Promise<CreateVentaResponse> {
  return apiFetch<CreateVentaResponse>('/api/ventas', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Outcome of the post-anular internal alert email. `null` when the
 * caller is an admin (PowerApps gate — admins don't get alerted about
 * their own action). Always present otherwise.
 */
export type AnularEmailResult =
  | { sent: true; to: string }
  | { sent: false; reason: 'no_recipient' | 'send_failed'; error?: string };

export type AnularVentaResponse = {
  id: string;
  autorizacionesAnuladas: number;
  email: AnularEmailResult | null;
};

/**
 * Soft-cancel a cotización: patches `Status_RO='Anulado'` on the header
 * and any pending `17.AutorizacionDescuentos` rows in one transactional
 * step so the approval queue doesn't end up with orphans pointing at a
 * cancelled venta.
 */
export async function anularVenta(id: string | number): Promise<AnularVentaResponse> {
  return apiFetch<AnularVentaResponse>(`/api/ventas/${id}/anular`, {
    method: 'POST',
  });
}

// ---------- edit ----------

export type EditVentaLinePayload = VentaLinePayload & {
  /** Present for existing detalle rows we're keeping; omitted for new rows. */
  detalleId?: string;
};

export type EditVentaHeaderPayload = {
  moneda: 'ARS' | 'MXN' | 'USD';
  subtotal: number;
  iva21: number;
  iva105: number;
  total: number;
};

export type EditVentaPayload = {
  header: EditVentaHeaderPayload;
  lines: EditVentaLinePayload[];
};

export type EditVentaResponse = {
  id: string;
  status: string;
  detallesCreados: number;
  detallesAnulados: number;
  descuentosAnulados: number;
  autorizacionesAnuladas: number;
};

/**
 * Persist edits to an existing (not-yet-accepted) cotización. The server
 * diffs the payload against SharePoint, patches/adds/soft-deletes
 * detalles, and soft-rewrites Descuentos + AutorizacionDescuentos so
 * stale approval rows aren't left behind (the original PowerApps bug).
 */
export async function editarVenta(
  id: string | number,
  payload: EditVentaPayload,
): Promise<EditVentaResponse> {
  return apiFetch<EditVentaResponse>(`/api/ventas/${id}/editar`, {
    method: 'POST',
    body: payload,
  });
}

// ---------- eliminar ----------

/**
 * Outcome enum the server returns so the UI can show the right dialog
 * without a second round-trip. Matches the branching in
 * `Screen_Ventas.pa.yaml`'s delete-button flow.
 */
export type EliminarVentaResponse =
  | {
      outcome: 'deleted_cotizacion';
      detallesAnulados: number;
      descuentosAnulados: number;
      autorizacionesAnuladas: number;
    }
  | {
      outcome: 'deleted_venta_direct';
      detallesAnulados: number;
      descuentosAnulados: number;
      autorizacionesAnuladas: number;
    }
  | {
      outcome: 'pending_approval_created';
      autorizacionId: string;
    }
  | {
      outcome: 'blocked_existing_request';
      autorizacionId: string;
    }
  | {
      outcome: 'blocked_status';
      error: string;
      message: string;
    };

/**
 * Soft-delete a cotización / venta. The server decides the path
 * (direct cascade vs. authorization request) based on Tipo_RO and
 * existing payments; the caller inspects `outcome` to show the right
 * confirmation or block dialog.
 */
export async function eliminarVenta(
  id: string | number,
): Promise<EliminarVentaResponse> {
  return apiFetch<EliminarVentaResponse>(`/api/ventas/${id}/eliminar`, {
    method: 'POST',
  });
}

// ---------- pagos ----------

export type SaveMediosPagoPayload = {
  moneda: 'ARS' | 'MXN' | 'USD';
  breakdown: {
    porcentajeFactura: number;
    iva21: number;
    iva105: number;
    montoFactura: number;
    ivaACobrar: number;
    totalFactura: number;
    montoCobroEfectivo: number;
    montoTotalCobro: number;
    /** Recargo MercadoPago por cuotas, ya incluido en totalFactura / montoTotalCobro. */
    recargoMp?: { cuotas: number; pct: number; monto: number } | null;
  };
  medios: Array<{
    /** Present when patching an existing 03.Pagos row. */
    pagoId?: string;
    medio: string;
    categoria: 'Local' | 'Internacional';
    pagoUnico: boolean;
    distPagos: boolean;
    monto: number;
    tipoCambio?: number;
    /** Equivalent in the "other" currency — written to `MontoDolar_P`. */
    montoDolar?: number;
    /** Medio con cuotas (MercadoPago): cantidad elegida. */
    cuotas?: number;
  }>;
  /** Existing 03.Pagos row ids to soft-cancel (Status_P='Inactivo'). */
  anularPagoIds: string[];
};

export type SaveMediosPagoResponse = {
  patched: string[];
  created: string[];
  anulados: string[];
};

/**
 * Save the medios de pago configuration + invoice breakdown for a venta.
 * Writes/patches rows in `03.Pagos` and patches the header's invoice
 * fields. Does not touch `Status_RO`.
 */
export async function saveMediosPago(
  id: string | number,
  payload: SaveMediosPagoPayload,
): Promise<SaveMediosPagoResponse> {
  return apiFetch<SaveMediosPagoResponse>(`/api/ventas/${id}/pagos/medios`, {
    method: 'POST',
    body: payload,
  });
}

export type RegistrarComprobantePayload = {
  kind: 'pago' | 'promesa';
  medioPago: string;
  medioPagoId?: string;
  categoria: 'Local' | 'Internacional';
  monto: number;
  tipoCambio?: number;
  /** Amount in the "other" currency — written to `Pesos_CP` (legacy column name). */
  pesos?: number;
  fechaCobro: string;
  fechaPago: string;
  esPagoFinal?: boolean;
  contieneFactura?: boolean;
  nroFactura?: string;
  /** Caja E-cheq: datos del cheque. */
  echeq?: { nroCheque: string; bancoEmisor: string; cuitLibrador: string; fechaPago: string };
};

export type RegistrarComprobanteResponse = {
  comprobanteId: string;
  statusCP: 'Pendiente de Confirmacion' | 'Pendiente de Cobro';
  statusRO: string;
};

/**
 * Create a `04.ComprobantePagos` row (pago or promesa). Bumps header
 * `Status_RO` from `Aceptado` to `Pendiente de Pago` if it's the first
 * comprobante registered. Admin later confirms via the Aprobaciones
 * module to advance Status_RO further.
 */
export async function registrarComprobante(
  id: string | number,
  payload: RegistrarComprobantePayload,
): Promise<RegistrarComprobanteResponse> {
  return apiFetch<RegistrarComprobanteResponse>(
    `/api/ventas/${id}/pagos/comprobantes`,
    { method: 'POST', body: payload },
  );
}

export type SolicitarFacturacionPreviaResponse = {
  ok: true;
  /** Ya había una solicitud en curso — el backend la devuelve en vez de 409. */
  alreadyPending?: boolean;
  /** El comprobante ya tenía la facturación previa aprobada. */
  alreadyApproved?: boolean;
  row?: { id: number; createdAt?: string; motivo?: string | null };
};

/**
 * Pide autorización (AR) para emitir la factura de este cobro ANTES de
 * cobrarlo. No cambia nada por sí sola: recién cuando un admin aprueba desde
 * Aprobaciones el comprobante entra temprano en la cola de Facturación. Si la
 * rechazan, el cobro sigue el flujo de siempre.
 */
export async function solicitarFacturacionPrevia(
  id: string | number,
  comprobanteId: string | number,
  motivo?: string,
): Promise<SolicitarFacturacionPreviaResponse> {
  return apiFetch<SolicitarFacturacionPreviaResponse>(
    `/api/ventas/${id}/pagos/comprobantes/${comprobanteId}/facturacion-previa`,
    { method: 'POST', body: motivo ? { motivo } : {} },
  );
}

export type FacturacionPreviaEstado = {
  /** Mismo id que usa la fila de la tabla de pagos (sharepoint_id). */
  comprobanteId: string;
  status: 'Pendiente' | 'Aprobado' | 'Rechazado' | 'Anulado';
  /** Motivo que dejó el SOLICITANTE al pedirla. */
  motivo: string | null;
  /** Motivo que dejó el administrador al RECHAZARLA. */
  motivoRechazo: string | null;
  resueltoAt: string | null;
  createdAt: string;
};

/**
 * Estado de las solicitudes de Facturación Previa de una venta, por
 * comprobante. Hace falta porque `FacturacionPrevia_CP` sólo se prende al
 * APROBAR: sin esto la pantalla no puede distinguir "nunca se pidió" de
 * "se pidió y la rechazaron".
 */
export async function getFacturacionPreviaEstados(
  id: string | number,
): Promise<FacturacionPreviaEstado[]> {
  const res = await apiFetch<{ items: FacturacionPreviaEstado[] }>(
    `/api/ventas/${id}/pagos/facturacion-previa`,
  );
  return res.items;
}

export type AnularComprobanteResponse = {
  comprobanteId: string;
  statusCP: 'Anulado';
};

/** Soft-cancel a comprobante that hasn't been confirmed yet. */
export async function anularComprobante(
  id: string | number,
  comprobanteId: string,
): Promise<AnularComprobanteResponse> {
  return apiFetch<AnularComprobanteResponse>(
    `/api/ventas/${id}/pagos/comprobantes/${comprobanteId}/anular`,
    { method: 'POST' },
  );
}

export type SolicitarAnulacionComprobanteResponse =
  | { outcome: 'pending_approval_created'; autorizacionId: string }
  | { outcome: 'blocked_existing_request'; autorizacionId: string };

/**
 * Cobro con factura emitida: su anulación va a la cola de Aprobaciones
 * (Eliminaciones) en vez de hacerse directo.
 */
export async function solicitarAnulacionComprobante(
  id: string | number,
  comprobanteId: string,
): Promise<SolicitarAnulacionComprobanteResponse> {
  return apiFetch<SolicitarAnulacionComprobanteResponse>(
    `/api/ventas/${id}/pagos/comprobantes/${comprobanteId}/solicitar-anulacion`,
    { method: 'POST' },
  );
}

export type EditarPromesaPayload = {
  medioPago: string;
  monto: number;
  /** dd/mm/yyyy */
  fechaCobro: string;
  tipoCambio?: number;
  pesos?: number;
};

/**
 * Edit a `Pendiente de Cobro` promesa. Allowed fields: medioPago,
 * monto, fechaCobro (and tipoCambio/pesos when the promesa was
 * originally USD). Server rejects edits on any other Status_P.
 */
export async function editarPromesa(
  id: string | number,
  comprobanteId: string,
  payload: EditarPromesaPayload,
): Promise<{ comprobanteId: string }> {
  return apiFetch(
    `/api/ventas/${id}/pagos/comprobantes/${comprobanteId}`,
    { method: 'PATCH', body: payload },
  );
}

export type CargarComprobantePayload = {
  medioPago: string;
  monto: number;
  /** dd/mm/yyyy */
  fechaPago: string;
  contieneFactura: boolean;
};

/**
 * Patch the comprobante's form fields prior to uploading the actual
 * file. The subsequent `uploadDocument(entityType='ComprobanteVenta')`
 * call triggers the Status_P/Status_RO transitions and stock consume
 * via `runPromesaUploadHook` on the server.
 */
export async function cargarComprobante(
  id: string | number,
  comprobanteId: string,
  payload: CargarComprobantePayload,
): Promise<{ comprobanteId: string }> {
  return apiFetch(
    `/api/ventas/${id}/pagos/comprobantes/${comprobanteId}/cargar`,
    { method: 'POST', body: payload },
  );
}

// ---------------------------------------------------------------------------
// Email cotización
// ---------------------------------------------------------------------------

export type EnviarCotizacionEmailPayload = {
  recipientEmail: string;
  recipientName?: string;
  fileName: string;
  pdf: Blob;
};

export type EnviarCotizacionEmailResponse =
  | { sent: true; to: string; bcc: string | null }
  | { sent: false; reason: 'no_recipient' | 'send_failed'; error?: string };

/**
 * Multipart upload — the cotización PDF is generated client-side
 * (jsPDF) so we POST it as a file. Server attaches it and forwards to
 * Graph. Bypasses `apiFetch` because that helper expects JSON bodies.
 */
export async function enviarCotizacionEmail(
  ventaId: string,
  payload: EnviarCotizacionEmailPayload,
): Promise<EnviarCotizacionEmailResponse> {
  const form = new FormData();
  form.append(
    'body',
    JSON.stringify({
      recipientEmail: payload.recipientEmail,
      recipientName: payload.recipientName ?? '',
      fileName: payload.fileName,
    }),
  );
  form.append('pdf', payload.pdf, payload.fileName);
  const res = await fetch(
    `/api/ventas/${encodeURIComponent(ventaId)}/email-cotizacion`,
    {
      method: 'POST',
      body: form,
      headers: authHeaders(),
    },
  );
  const json = (await res.json().catch(() => null)) as
    | EnviarCotizacionEmailResponse
    | null;
  // 400 (`no_recipient`) and 502 (`send_failed`) both come back with a
  // structured `{ sent: false, reason, error? }` body — return it so the
  // caller can show a real toast (e.g., Graph's "mailbox inactive"
  // message) instead of a bare `failed: 502`. Only throw when there's
  // no JSON body to interpret (parse failure, 401, network blip).
  if (!json) {
    throw new Error(`Enviar cotización failed: ${res.status}`);
  }
  return json;
}

// ---------------------------------------------------------------------------
// Share cotización link (WhatsApp)
// ---------------------------------------------------------------------------

export type ShareCotizacionLinkPayload = {
  fileName: string;
  pdf: Blob;
};

/**
 * Upload the cotización PDF to OneDrive and get back an anonymous,
 * view-only sharing URL. Used by the WhatsApp button so the customer
 * receives a real link instead of a plain-text price dump (QA #5).
 */
export async function shareCotizacionLink(
  ventaId: string,
  payload: ShareCotizacionLinkPayload,
): Promise<{ shareUrl: string }> {
  const form = new FormData();
  form.append('body', JSON.stringify({ fileName: payload.fileName }));
  form.append('pdf', payload.pdf, payload.fileName);
  const res = await fetch(
    `/api/ventas/${encodeURIComponent(ventaId)}/share-cotizacion-link`,
    {
      method: 'POST',
      body: form,
      headers: authHeaders(),
    },
  );
  const json = (await res.json().catch(() => null)) as
    | { shareUrl: string }
    | null;
  if (!res.ok || !json?.shareUrl) {
    throw new Error(`shareCotizacionLink failed: ${res.status}`);
  }
  return json;
}

// --------------------------------------------------------------------------
// Factura % approval (MX only)
// --------------------------------------------------------------------------

export type FacturaPercentageAuthorizationPending = {
  id: number;
  porcentaje_solicitado: number;
  porcentaje_anterior: number | null;
  umbral: number;
  motivo: string | null;
  created_at: string;
};

export type FacturaPercentageAuthorizationResolved = {
  id: number;
  porcentaje_solicitado: number;
  porcentaje_aplicado: number | null;
  status: 'Aprobado' | 'Rechazado';
  resuelto_at: string | null;
};

export type FacturaPercentageAuthorizationStatus = {
  pending: FacturaPercentageAuthorizationPending | null;
  lastResolved: FacturaPercentageAuthorizationResolved | null;
  /** Country-scoped minimum percentage that doesn't need approval.
   *  0 on AR (feature disabled) so frontend logic can short-circuit. */
  umbral: number;
};

export async function getFacturaPercentageAuthorization(
  ventaId: string,
): Promise<FacturaPercentageAuthorizationStatus> {
  return apiFetch<FacturaPercentageAuthorizationStatus>(
    `/api/ventas/${encodeURIComponent(ventaId)}/factura-percentage-authorization`,
  );
}

export type RequestFacturaPercentageAuthorizationResponse =
  | {
      ok: true;
      alreadyPending?: boolean;
      row: {
        id: number;
        porcentaje_solicitado: number;
        porcentaje_anterior: number | null;
        umbral: number;
        created_at: string;
      };
    }
  | { error: 'above_threshold'; message: string; umbral: number }
  | { error: 'mx_only' }
  | { error: 'venta_not_found' };

/** MX Nova "horno usado" context for a venta. AR ventas + non-Nova MX
 *  ventas return with `applies=false` so the UI short-circuits. */
export type UsadoContext = {
  costoContable: number;
  totalFactura: number;
  /** Current persisted % factura on the venta header — surfaced so the
   *  cotización editor can locally project totalFactura when staging
   *  discount edits, without an extra fetch. */
  porcentajeFactura: number;
  comisionNova: number;
  ivaMx: number;
  facturaMinimo: number;
  usado: boolean;
  applies: boolean;
  /** True when ANY factura% or discount approval on this venta was
   *  rejected while sitting in usado territory (`was_usado=true` on
   *  the approval row). Drives the Pagos hard-block: after a
   *  rejection the seller can't try another usado-territory save. */
  hasRejectedUsadoApproval: boolean;
};

export async function getUsadoContext(ventaId: string): Promise<UsadoContext> {
  return apiFetch<UsadoContext>(
    `/api/ventas/${encodeURIComponent(ventaId)}/usado-context`,
  );
}

/** Cost buildup of the MX Nova Factura Interna (Valor Contable) total —
 *  costo contable → × multiplicador → + IVA → total. MX only. */
export type FacturaInternaBreakdown = {
  costoContable: number;
  multiplicador: number;
  subtotal: number;
  ivaRate: number;
  iva: number;
  total: number;
  /** True only for Nova ventas — the Valor Contable is a Nova-flow concept.
   *  The UI hides the breakdown on non-Nova MX ventas. */
  applies: boolean;
};

export async function getFacturaInternaBreakdown(
  ventaId: string,
): Promise<FacturaInternaBreakdown> {
  return apiFetch<FacturaInternaBreakdown>(
    `/api/ventas/${encodeURIComponent(ventaId)}/factura-interna-breakdown`,
  );
}

/**
 * Count of gifts (regalo lines) a vendor has given in a month (mm/yyyy),
 * across non-anulado ventas in the active country. Drives the monthly
 * gift-quota gate. Defaults server-side to the authenticated vendor +
 * current month when args are omitted.
 */
export async function getRegalosMes(
  vendedorApp: string,
  mesAnio: string,
): Promise<number> {
  const res = await apiFetch<{ count: number }>(
    `/api/ventas/regalos-mes?vendedorApp=${encodeURIComponent(vendedorApp)}` +
      `&mesAnio=${encodeURIComponent(mesAnio)}`,
  );
  return res.count;
}

/** Admin-only: clear the usado save hard-block for this venta. */
export async function overrideUsadoLock(ventaId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/ventas/${encodeURIComponent(ventaId)}/usado-lock/override`,
    { method: 'POST' },
  );
}

/** Bulk: SP ids of ventas in the active country with a Pendiente
 *  factura% authorization. Powers the "Pend. Aprob. %F" badge on the
 *  ventas list. Available to every authenticated role. */
export async function listVentasPendingFacturaPercentage(): Promise<number[]> {
  const res = await apiFetch<{ ventaSharepointIds: number[] }>(
    '/api/ventas/factura-percentage-authorizations/pending',
  );
  return res.ventaSharepointIds;
}

export async function requestFacturaPercentageAuthorization(
  ventaId: string,
  args: {
    porcentajeSolicitado: number;
    porcentajeAnterior?: number | null;
    motivo?: string;
  },
): Promise<RequestFacturaPercentageAuthorizationResponse> {
  return apiFetch<RequestFacturaPercentageAuthorizationResponse>(
    `/api/ventas/${encodeURIComponent(ventaId)}/factura-percentage-authorization`,
    {
      method: 'POST',
      body: {
        porcentajeSolicitado: args.porcentajeSolicitado,
        porcentajeAnterior: args.porcentajeAnterior ?? null,
        motivo: args.motivo,
      },
    },
  );
}
